import React, { FC, useMemo } from "react";

import { usePagedRows } from "../../../components/pagination/usePagedRows";
import { WorkModel } from "../../../factories/works/models/work.model";
import { exportDataToExcel } from "../../../utils/ExportData";
import { askTable, filtersText } from "../ask/reportAsk.utils";
import {
  formatDate,
  peopleOf,
  STATUS_COLORS,
  statusGroup,
  timeInStatus,
} from "../reports.utils";
import { scopeUserIds, useReportsAccess } from "../reportsAccess";
import { useViewExtra } from "../saved/SavedViewContext";
import {
  ChoiceMenu,
  PeopleClientTypeFilters,
  useReportFilters,
} from "../ui/ReportFilters";
import {
  AssigneeCell,
  Card,
  Empty,
  Kpi,
  KpiGrid,
  LoadError,
  Loading,
  Note,
  NUM,
  ReportPage,
  StackedBar,
  StatusPill,
  TableBox,
  TD,
  TH,
  useFocusRows,
  WorkLink,
} from "../ui/ReportUi";
import { useAllWorks } from "../useReportData";

/** The assignee as the table shows it (see AssigneeCell), for "Ask about this report". */
const assigneeText = (w: WorkModel) => {
  const people = peopleOf(w);
  if (!people[0]?.id) {
    return w.roleName ? `Role: ${w.roleName}` : "Not assigned";
  }
  return people.map((p) => p.name).join(", ");
};

type Threshold = "7" | "14" | "30" | "0";
const THRESHOLDS: { value: Threshold; label: string }[] = [
  { value: "7", label: "More than 7 days" },
  { value: "14", label: "More than 14 days" },
  { value: "30", label: "More than 30 days" },
  { value: "0", label: "Any time" },
];

export const TimeInStatusReport: FC = () => {
  const access = useReportsAccess();
  const state = useReportFilters();
  const { filters } = state;
  const [threshold, setThreshold] = useViewExtra<Threshold>(
    "threshold",
    "14",
    THRESHOLDS,
    "In status for",
  );
  const works = useAllWorks({
    hideGreenStatus: true,
    userIds: scopeUserIds(access, filters.userIds),
    customerIds: filters.clientIds,
    worktypeIds: filters.workTypeIds,
  });
  const minDays = threshold === "0" ? 0 : Number(threshold) + 1;
  const data = useMemo(
    () => timeInStatus(works.data, minDays),
    [works.data, minDays],
  );
  const maxAvg = Math.max(1, ...data.byStatus.map((s) => s.avgDays));
  const v = (x: React.ReactNode) => (works.isLoading ? "…" : x);
  const round = (n: number) => Math.round(n * 10) / 10;
  const thresholdLabel = THRESHOLDS.find(
    (t) => t.value === threshold,
  )?.label.toLowerCase();

  const onExport = () =>
    exportDataToExcel(
      data.rows.map(({ work, days }) => ({
        Work: work.workName,
        Client: work.customerName,
        Status: work.jobSubStatus || work.jobStatus,
        "In this status since": formatDate(work.statusUpdatedAt),
        Days: days,
        Assignee: peopleOf(work)
          .map((p) => p.name)
          .join(", "),
        "Due date": formatDate(work.dueDate),
      })),
      "Time in status",
    );

  const tableName =
    threshold === "0"
      ? "Open work, longest in its status first"
      : `Work in the same status ${thresholdLabel}`;
  const shownRows = useFocusRows(tableName, data.rows, (r) => r.work.workId);
  const { pageRows, pager } = usePagedRows(shownRows);

  const ask = useMemo(
    () => ({
      title: "Time in status",
      path: "/reports/time-in-status",
      description:
        "How long open work has been sitting in its current status, counted from its last status change.",
      filters: filtersText([
        `In status for: ${
          THRESHOLDS.find((t) => t.value === threshold)?.label
        }`,
        !access.firmWide && "Only your own work",
      ]),
      kpis: [
        { label: "Stuck", value: String(data.rows.length) },
        {
          label: "Average time in status",
          value: `${round(data.avgDays)} days`,
        },
        {
          label: "Longest",
          value: data.longest
            ? `${data.longest.days} days (${data.longest.work.workName} · ${data.longest.work.customerName})`
            : "—",
        },
        {
          label: "Slowest status",
          value: data.slowest
            ? `${data.slowest.group} (${round(
                data.slowest.avgDays,
              )} days on average)`
            : "—",
        },
      ],
      tables: [
        askTable(
          "Average days in each status",
          data.byStatus,
          [
            {
              key: "status",
              label: "Status",
              kind: "text",
              value: (s) => s.group,
            },
            {
              key: "avgDays",
              label: "Average days",
              kind: "number",
              value: (s) => round(s.avgDays),
            },
          ],
          (s) => s.group,
        ),
        askTable(
          tableName,
          data.rows,
          [
            {
              key: "work",
              label: "Work",
              kind: "text",
              value: (r) => r.work.workName,
            },
            {
              key: "client",
              label: "Client",
              kind: "text",
              value: (r) => r.work.customerName,
            },
            {
              key: "status",
              label: "Status",
              kind: "text",
              value: (r) => r.work.jobSubStatus || r.work.jobStatus,
            },
            {
              key: "since",
              label: "In this status since",
              kind: "date",
              value: (r) => formatDate(r.work.statusUpdatedAt),
            },
            {
              key: "days",
              label: "Days",
              kind: "number",
              value: (r) => r.days,
            },
            {
              key: "assignee",
              label: "Assignee",
              kind: "text",
              value: (r) => assigneeText(r.work),
            },
            {
              key: "due",
              label: "Due date",
              kind: "date",
              value: (r) => formatDate(r.work.dueDate),
            },
          ],
          (r) => r.work.workId,
          (r) => `/work/${r.work.workId}`,
        ),
      ],
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, tableName, threshold, access.firmWide],
  );

  return (
    <ReportPage
      title={"Time in status"}
      description={
        "How long open work has been sitting in its current status. The longest first, so bottlenecks stand out."
      }
      onExport={data.rows.length ? onExport : undefined}
      ask={works.isLoading ? undefined : ask}
      askSuggestions={[
        "Which work has been stuck the longest?",
        "Which status is the bottleneck?",
        "Who has the most stuck work?",
      ]}
      filters={
        <>
          <ChoiceMenu
            label={"In status for"}
            options={THRESHOLDS}
            value={threshold}
            onChange={setThreshold}
          />
          <PeopleClientTypeFilters state={state} showPeople={access.firmWide} />
        </>
      }>
      {works.isError && <LoadError />}
      <KpiGrid>
        <Kpi
          label={"Stuck"}
          value={v(data.rows.length)}
          note={`In the same status ${thresholdLabel}`}
          tone={"red"}
        />
        <Kpi
          label={"Average time in status"}
          value={v(`${round(data.avgDays)} days`)}
          note={"Open work with a known status date"}
        />
        <Kpi
          label={"Longest"}
          value={v(data.longest ? `${data.longest.days} days` : "—")}
          note={
            data.longest
              ? `${data.longest.work.workName} · ${data.longest.work.customerName}`
              : undefined
          }
        />
        <Kpi
          label={"Slowest status"}
          value={v(data.slowest ? data.slowest.group : "—")}
          note={
            data.slowest
              ? `${round(data.slowest.avgDays)} days on average`
              : undefined
          }
        />
      </KpiGrid>

      <Card title={"Average days in each status"}>
        {works.isLoading ? (
          <Loading />
        ) : works.isError ? null : !data.byStatus.length ? (
          <Empty text={"No open work."} />
        ) : (
          <div className={"flex flex-col gap-3"}>
            {data.byStatus.map((s) => (
              <div
                key={s.group}
                className={"grid items-center gap-3.5"}
                style={{ gridTemplateColumns: "130px minmax(0, 1fr) 90px" }}>
                <span>
                  <StatusPill group={s.group} />
                </span>
                <StackedBar
                  max={maxAvg}
                  parts={[
                    {
                      value: s.avgDays,
                      color: STATUS_COLORS[s.group],
                      label: `${s.group} days`,
                    },
                  ]}
                />
                <span className={"text-right text-[13px] font-bold"}>
                  {round(s.avgDays)} days
                </span>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card
        title={
          threshold === "0"
            ? "Open work, longest in its status first"
            : `Work in the same status ${thresholdLabel}`
        }>
        {works.isLoading ? (
          <Loading />
        ) : works.isError ? null : !data.rows.length ? (
          <Empty text={"Nothing has been stuck that long."} />
        ) : (
          <TableBox pager={pager}>
            <thead>
              <tr>
                <th scope={"col"} className={TH}>
                  Work
                </th>
                <th scope={"col"} className={TH}>
                  Client
                </th>
                <th scope={"col"} className={TH}>
                  Status
                </th>
                <th scope={"col"} className={TH}>
                  In this status since
                </th>
                <th scope={"col"} className={`${TH} ${NUM}`}>
                  Days
                </th>
                <th scope={"col"} className={TH}>
                  Assignee
                </th>
                <th scope={"col"} className={TH}>
                  Due date
                </th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map(({ work, days }) => (
                <tr key={work.workId}>
                  <td className={TD}>
                    <WorkLink id={work.workId} name={work.workName} />
                  </td>
                  <td className={TD}>{work.customerName}</td>
                  <td className={TD}>
                    <StatusPill
                      group={statusGroup(work)}
                      label={work.jobSubStatus || work.jobStatus}
                    />
                  </td>
                  <td className={TD}>{formatDate(work.statusUpdatedAt)}</td>
                  <td
                    className={`${TD} ${NUM} font-extrabold ${
                      days >= 30
                        ? "text-[#B42318]"
                        : days > 14
                        ? "text-[#8A4100]"
                        : ""
                    }`}>
                    {days}
                  </td>
                  <td className={TD}>
                    <AssigneeCell
                      people={peopleOf(work)}
                      role={work.roleName}
                    />
                  </td>
                  <td className={TD}>{formatDate(work.dueDate)}</td>
                </tr>
              ))}
            </tbody>
          </TableBox>
        )}
      </Card>

      <Note>
        Days are counted from the last status change of each work item.
        {data.noDate > 0 &&
          ` ${data.noDate} open work ${
            data.noDate === 1 ? "item has" : "items have"
          } no recorded status change yet (usually older work), so ${
            data.noDate === 1 ? "it is" : "they are"
          } not counted here.`}
      </Note>
    </ReportPage>
  );
};
